import { isValidObjectId } from "src/app/support/object-id";

type WorkOrderResultA = {
  name: string;
  tests: Array<{
    name: string;
    code: string;
    shortName?: string;
    value: string;
    unit?: string;
    minRange?: string;
    maxRange?: string;
  }>;
};

type WorkOrderResultB = {
  aeTitle: string;
  stationName: string;
  modality: string;
  description: string;
  plannedAt: string;
  externalId: string;
};

export enum WorkOrderStatus {
  COMPLETED = "completed",
  ACCEPTED = "accepted",
  TO_ACCEPT = "to_accept",
  IN_PROGRESS = "in-progress",
  DELETED = "deleted",
  CREATED = "created"
}

export type WorkOrderItem = {
  code: string;
  createdAt: string;
  deviceId: string;
  externalId: string;
  name: string;
  type: "internal" | "external";
  id: string;

  medicalServiceId?: string;

  bookItem?: {
    id: string;
    emittedAt?: string;
    invoiceId?: string;
    invoiceNumber?: string;
  };
};

export interface WorkOrder<
  IntegrationKey extends string = string,
  IntegrationData = any
> {
  id: string;
  createdAt: string;

  createdBy: {
    id: string;
    name: string;
  };

  description?: string;

  status?: WorkOrderStatus;
  results?: Array<WorkOrderResultA | WorkOrderResultB>;
  metadata?: Record<IntegrationKey, IntegrationData>;

  number: string;
  name: string;
  type: CategoryType;

  externalId?: string;

  sentAt?: string;

  acceptedAt?: string;

  submittedAt?: string;
  completedAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  canceledAt?: string;

  customer?: {
    id: string;
    number: string;
    firstName?: string;
    lastName?: string;
    businessName?: string;
  };

  patient?: {
    id: string;
    number: string;
    name: string;
    species?: string;
    breed?: string;
    sex: SexType;
  };

  urgent: boolean;

  ticket?: {
    id: string;
    number: string;
  };

  notes?: string;

  origin?: {
    id: string;
    name: string;
    type: "integration";
  };

  externalRevenue: boolean;

  items: WorkOrderItem[];

  needToBeReconciliated?: boolean;
}

export interface Exam {
  id: string;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
  };
  lisId?: string;
  name: string;
  code?: string;

  status?: string;

  sentAt?: string;
  acceptedAt?: string;
  inProgressAt?: string;
  partialAt?: string;
  completedAt?: string;
  validatedAt?: string;
  canceledAt?: string;

  addedAt?: string;
  updatedAt?: string;

  tests?: Array<ExamTest>;

  attachments?: Array<ExamAttachment>;

  viewStudyLink?: string;
}

export interface ExamTest {
  id: string;

  createdAt: string;
  createdBy?: {
    id: string;
    name: string;
  };

  name: string;
  shortName?: string;
  code: string;
  value: string;
  unit?: string;
  minRange?: string;
  maxRange?: string;
  formula?: string;
  type?: string;
}

export interface ExamAttachment {
  id: string;
  name?: string;
  description?: string;
  storageId: string;
  size?: number;
}

export enum CategoryType {
  LABORATORY = "laboratory",
  DIAGNOSTIC = "diagnostic"
}

export enum StatusType {
  TO_ACCEPT = "to-accept",
  ACCEPTED = "accepted",
  SENT = "sent",
  IN_PROGRESS = "in-progress",
  COMPLETED = "completed",
  CANCELED = "canceled"
}

export type SexType = "female" | "male" | "unknown";

export function workOrderNeedReconcile(
  workOrder: WorkOrder | undefined
): boolean {
  if (!workOrder) {
    return false;
  }

  return (
    !workOrder.customer?.id ||
    !workOrder.patient?.id ||
    !isValidObjectId(workOrder.customer.id) ||
    !isValidObjectId(workOrder.patient.id)
  );
}
